import { useState } from 'react';
import Field from './Field';
import { ErrorBanner } from './Feedback';
import useForm from '../hooks/useForm';
import * as codeRules from '../validation/codeRules';
import { api } from '../api/client';

/**
 * Enter Activation Code — one field and a button, as the design specifies.
 *
 * <p>Typing only upper-cases; the hyphens are put in on blur. Canonicalising on every
 * keystroke would reshape the field mid-edit — deleting one character would drop the
 * hyphens and move the cursor — so the tidy-up waits until the student has finished.
 */
export default function ActivateBookForm({ onActivated, autoFocus = false }) {
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: { code: '' },
    validators: { code: codeRules.activationCode },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await api.activateCode(codeRules.normalizeCode(values.code));
      form.reset();
      onActivated(result);
    } catch (err) {
      // Every activation failure is about this one field, so it renders there —
      // "already used", "not yours", "already in your library" are all specific.
      if (!form.applyServerErrors(err, codeRules.ACTIVATION_SERVER_CODES)) setError(err);
    }
  });

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      <form className="stack" {...form.formProps} onSubmit={submit}>
        <Field
          label="Activation code"
          id="code"
          required
          error={form.errorFor('code')}
          hint="Printed on the slip your teacher gave you, like ABCD-EFGH-JKMN"
        >
          <input
            className="input code-text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
            maxLength={20}
            placeholder="ABCD-EFGH-JKMN"
            autoFocus={autoFocus}
            {...form.field('code', {
              onChange: (event) => form.setValue('code', event.target.value.toUpperCase()),
              onBlur: () => {
                form.setValue('code', codeRules.normalizeCode(form.values.code));
                form.markTouched('code');
              },
            })}
          />
        </Field>

        <div>
          <button type="submit" className="btn btn-primary" disabled={form.submitting}>
            {form.submitting ? 'Activating…' : 'Activate'}
          </button>
        </div>
      </form>
    </>
  );
}
